import React from 'react';

import { Button, DynamicIconWrapper, Icons, Tooltip } from '@components/common';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { checkGroupPanelAccess } from '@utils/permissions';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IAssetRentRequest, IAssetRentRequestCardItem } from '@interfaces/rent/assetRent';

import {
	ECommonRequestStatusesForAssetRequest,
	ERequestItemChanges,
	ERequestItemChangesAcceptance,
} from '@enums/asset';

import colors from '@constants/colors';
import { mapGroupAccess } from '@constants/menuGroupAccess';
import { viewOnMapTooltip } from '@components/routes/AdminPanel/AssetProfile/constants';

import { ApprovalsButton } from './ApprovalButton';

import styles from './RequestTable.module.scss';

const {
	declined,
	cancelled,
	ended,
	done,
	contract,
	sentForAcceptance,
	accepted,
} = ECommonRequestStatusesForAssetRequest;

export interface IActionCellForAssetRentRequestTableParams {
	asset: IAssetRentRequestCardItem;
	handleViewOnMap: (assetId: string) => Promise<void>;
	openProposeDatesModal: (selectedItem: TSelectOption) => void;
	acceptProposedAsset: any;
	declineProposedAsset: any;
	isAO: boolean;
	handleOpenApprovalInfo: (asset: IAssetRentRequestCardItem) => void;
	request: IAssetRentRequest;
	removeProposedAsset: (id: string) => void;
	openProfileHandler: (assetDbId: string) => void;
	isAssetItemsLoading?: boolean;
}

export function ActionCellForAssetRentRequestTable({
	asset,
	handleViewOnMap,
	openProposeDatesModal,
	openProfileHandler,
	acceptProposedAsset,
	declineProposedAsset,
	isAO,
	handleOpenApprovalInfo,
	request,
	removeProposedAsset,
	isAssetItemsLoading,
}: IActionCellForAssetRentRequestTableParams) {
	const { accessPermission } = useAppSelector(auth);

	const { assetOwnerManageRequests } = accessPermission;

	const isProposed = !!asset.proposalInfo;
	const isProposedAssetInPending = asset.proposalInfo?.status?.name === ERequestItemChangesAcceptance.pending;
	const isProposedDeclined = asset.proposalInfo?.status?.name === ERequestItemChangesAcceptance.refused;
	const isProposedAccepted = asset.proposalInfo?.status?.name === ERequestItemChangesAcceptance.approved;
	const totalApprovingUnits = asset.approved + asset.disapproved + asset.pending;
	const isProposedDates = asset.proposalInfo?.type.name === ERequestItemChanges.changedDates;
	const isProposedAsset = asset.proposalInfo?.type.name === ERequestItemChanges.proposedAsset;

	const { requestStatus: { name: requestStatus } } = request;

	const handleViewOnClick = () => {
		if (!asset?.assetDbId || !asset?.showOnMap) return;

		if (handleViewOnMap) void handleViewOnMap(asset.assetDbId);
	};

	const handleRemoveProposedAsset = () => removeProposedAsset(asset.id);

	const handleChangeDates = () => {
		if (openProposeDatesModal) {
			openProposeDatesModal({
				value: asset.id,
				label: asset.assetId,
				id: asset.assetDbId,
			});
		}
	};

	const handleAcceptProposedAsset = () => {
		acceptProposedAsset({ assetId: asset.id });
	};

	const handleDeclineProposedAsset = () => {
		declineProposedAsset({ assetId: asset.id });
	};

	const isNoApprovalUnits = !totalApprovingUnits;

	const openApprovalInfo = () => {
		if (isNoApprovalUnits || isProposedAssetInPending || !asset?.showOnMap) return;

		handleOpenApprovalInfo(asset);
	};

	const openAssetProfile = () => {
		if (asset?.assetDbId) openProfileHandler(asset.assetDbId);
	};

	const proposalInfoStatusName = asset.proposalInfo?.status?.name;

	const isAssetAvailable = asset.periodAvailability;
	const isMapModuleAvailable = checkGroupPanelAccess(accessPermission, mapGroupAccess);

	const isEmptyApprovalTooltipDisabled = !isAO;
	const isProposedAssetRemovable = isProposed && isAO && assetOwnerManageRequests;
	const isShowApprovalBtn = isProposed && isProposedAsset
		? proposalInfoStatusName === ERequestItemChangesAcceptance.approved
		: proposalInfoStatusName !== ERequestItemChangesAcceptance.pending;

	const isAbleToChangeDates = assetOwnerManageRequests
		&& requestStatus !== sentForAcceptance
		&& requestStatus !== declined
		&& requestStatus !== cancelled
		&& requestStatus !== accepted
		&& requestStatus !== contract
		&& requestStatus !== done
		&& requestStatus !== ended
		&& isShowApprovalBtn
		&& isAssetAvailable;

	return (
		<div className={styles.approvedActions}>
			<Tooltip content='View asset profile'>
				<div
					className={styles.viewIcon}
					onClick={openAssetProfile}
				>
					<DynamicIconWrapper
						icon={Icons.viewIcon}
						isDisabled={!asset?.assetDbId}
					/>
				</div>
			</Tooltip>
			{isMapModuleAvailable && (
				<Tooltip
					content={viewOnMapTooltip}
					isDisabled={!viewOnMapTooltip}
				>
					<div
						className={styles.icon}
						onClick={handleViewOnClick}
					>
						<DynamicIconWrapper
							icon={Icons.location}
							isDisabled={!asset?.assetDbId || !asset?.showOnMap}
						/>
					</div>
				</Tooltip>
			)}
			{isAO && isAbleToChangeDates
				&& <Tooltip
					content='Change dates'
					isDisabled={!isAO}
				>
					<div
						className={styles.icon}
						onClick={handleChangeDates}>
						<DynamicIconWrapper
							icon={Icons.calendarWhite}
						/>
					</div>
				</Tooltip>
			}
			{isProposed && !isProposedDeclined && !isProposedAccepted
				? !isAO
					? <div className={styles.ButtonsContainer}>
						<Button
							customColors={{
								background: colors.green10,
								font: colors.green70,
							}}
							onClick={handleAcceptProposedAsset}
							text='Accept'
						/>
						<Button
							customColors={{
								background: colors.pink10,
								font: colors.pink60,
							}}
							onClick={handleDeclineProposedAsset}
							text='Refuse'
						/>
					</div>
					: isProposedAssetRemovable
						? <>
							<Tooltip
								content={isProposedDates ? 'Revoke date change' : 'Remove proposed asset'}
							>
								<div
									className={styles.icon}
									onClick={handleRemoveProposedAsset}
								>
									<DynamicIconWrapper
										icon={isProposedDates ? Icons.resetIcon : Icons.delete}
									/>
								</div>
							</Tooltip>
							{isShowApprovalBtn && isAssetAvailable
								&& <ApprovalsButton
									asset={asset}
									isAO={isAO}
									isAssetItemsLoading={isAssetItemsLoading}
									isAssetShownOnMap={asset?.showOnMap}
									isEmptyApprovalTooltipDisabled={isEmptyApprovalTooltipDisabled}
									isNoApprovalUnits={isNoApprovalUnits}
									openApprovalInfo={openApprovalInfo}
									totalApprovingUnits={totalApprovingUnits}
								/>
							}
						</>
						: isShowApprovalBtn && isAssetAvailable
						&& <ApprovalsButton
							asset={asset}
							isAO={isAO}
							isAssetItemsLoading={isAssetItemsLoading}
							isAssetShownOnMap={asset?.showOnMap}
							isEmptyApprovalTooltipDisabled={isEmptyApprovalTooltipDisabled}
							isNoApprovalUnits={isNoApprovalUnits}
							openApprovalInfo={openApprovalInfo}
							totalApprovingUnits={totalApprovingUnits}
						/>
				: isShowApprovalBtn && isAssetAvailable
				&& <ApprovalsButton
					asset={asset}
					isAO={isAO}
					isAssetItemsLoading={isAssetItemsLoading}
					isAssetShownOnMap={asset?.showOnMap}
					isEmptyApprovalTooltipDisabled={isEmptyApprovalTooltipDisabled}
					isNoApprovalUnits={isNoApprovalUnits}
					openApprovalInfo={openApprovalInfo}
					totalApprovingUnits={totalApprovingUnits}
				/>
			}
		</div>
	);
}
